
    // IMPORTS
    import { defineComponent, computed, ref, watch } from 'vue'
    import router from '@/router'
    import { useRoute } from 'vue-router'
    import { useUserStore } from '@/store/user'
    import { useSparePartsStore } from '@/store/spareParts'
    import { useShoppingCartStore } from '@/store/shoppingCart'
    import { NotificationDTO, useNotificationStore } from '@/store/notification'
    import { buildMaterialLocalization, buildUnitLocalization, getKendoShortDateFormat, OrderProposalApi } from '@/util'

    import { Button } from '@progress/kendo-vue-buttons'
    import { GridColumnProps } from '@progress/kendo-vue-grid'
    import { DialogActionsBar } from '@progress/kendo-vue-dialogs'
    import AppDialog from '@/components/common/app-dialog.vue'
    import { Card, CardHeader, CardTitle, CardBody } from '@progress/kendo-vue-layout'
    import { DatePicker } from '@progress/kendo-vue-dateinputs'
    import { Input } from '@progress/kendo-vue-inputs'
    import LoaderGuard from '@/components/common/loader-guard.vue'
    import Indicator from '@/components/common/my-indicator.vue'
    import PurchaseButton from '@/components/common/purchase-button.vue'
    import MyIcon from '@/components/common/my-icon.vue'
    import MyGrid from '@/components/grid/my-grid.vue'
    import MaterialPicture from '@/components/common/material-picture.vue'
    import ScrollArrows from '@/components/common/scroll-arrows.vue'
    import OrderProposalUrlDialog from '@/components/shopping-cart/order-proposal-url-dialog.vue'

    import { buildPriceFormatter, IMaterialDTO, IMaterialPriceData } from '@/types/material'
    import { IShoppingCartDTO, IShoppingCartItemDTO, IOrderProposalDTO, useCartPriceCalculator } from '@/types/shoppingCart'
    import { DbConstants } from '@/const'
    import { IBomHeaderDTO } from '@/types/bom'
    import { useBrowserStateStore } from '@/store/browserState'

    interface IShoppingCartRow extends IMaterialPriceData {
        materialNumber: number,
        shortText?: string,
        salesText?: string,
        partnerMaterialNo?: string,
        availableQty: number,
        count: number,
        countUnitText: string,
        priceQtyUnit: string,
        hasPriceOnRequest: boolean,
        priceText: string,
        amount: number,
        amountText: string,
        isIncludedPart: boolean,
        serialNumbers: string[],
        selected: boolean,
        materialDTO: IMaterialDTO | null
    }

    export default defineComponent({
        name: 'shoppingCart',

        components: {
            MyGrid,
            MaterialPicture,
            LoaderGuard,
            'k-button': Button,
            PurchaseButton,
            Indicator,
            MyIcon,
            AppDialog,
            DialogActionsBar,
            DatePicker,
            'k-input': Input,
            Card,
            CardHeader,
            CardTitle,
            CardBody,
            ScrollArrows,
            OrderProposalUrlDialog
        },

        setup() {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const route = useRoute()
            const userStore = useUserStore()
            const shoppingCartStore = useShoppingCartStore()
            const sparePartStore = useSparePartsStore()
            const notificationStore = useNotificationStore()
            const browserState = useBrowserStateStore()
            const orderProposalApi = new OrderProposalApi()

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const isUpdating = ref(false)
            const isResetCartConfirmationVisible = ref<boolean>(false)
            const isSendCartConfirmationVisible = ref<boolean>(false)

            // Named cart mode
            const orderProposal = ref<IOrderProposalDTO | null>(null)
            const proposalError = ref(false)
            const selectedMaterials = ref<Set<number>>(new Set())
            const isProposalUrlDialogVisible = ref(false)

            // Create order proposal dialog
            const isCreateProposalDialogVisible = ref(false)
            const createProposalName = ref('')
            const createProposalExpDate = ref<Date | null>(getDefaultExpirationDate())
            const createdProposalCartId = ref<string | null>(null)
            const createdProposalOrderProposalNo = ref<number>(0)
            const createdProposalName = ref<string>('')

            // Scroll arrows
            const cartCardRef = ref<any>(null)

            const publicData = {
                isUpdating, isResetCartConfirmationVisible, isSendCartConfirmationVisible,
                orderProposal, proposalError, isProposalUrlDialogVisible,
                isCreateProposalDialogVisible, createProposalName, createProposalExpDate,
                createdProposalCartId, createdProposalOrderProposalNo, createdProposalName,
                kendoDateFormat: getKendoShortDateFormat(),
                cartCardRef
            }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const { userCurrency, priceFormatter } = buildPriceFormatter()
            const cartPriceCalculator = useCartPriceCalculator()

            const isNarrowScreen = computed(() => (browserState.resolution?.width ?? 1200) < 1000)
            const isAdmin = computed(() => userStore.isAdmin)
            const proposalId = computed(() => route.query.id as string | undefined)
            const isNamedMode = computed(() => !!proposalId.value)

            const allSpareParts = computed(() => sparePartStore.spareParts)
            const sparePartMap = computed(() => sparePartStore.sparePartMap)

            const activeCart = computed<IShoppingCartDTO | null>(() =>
                isNamedMode.value ? orderProposal.value : shoppingCartStore.shoppingCart
            )

            const hasLoaded = computed(() => {
                if (isNamedMode.value) {
                    return proposalError.value || (orderProposal.value != null &&
                        userStore.userCurrencyHasLoaded &&
                        (orderProposal.value.items.length == 0 || (allSpareParts.value?.length ?? 0) > 0))
                }

                return activeCart.value != null &&
                    userStore.userCurrencyHasLoaded &&
                    (activeCart.value.items.length == 0 || (allSpareParts.value?.length ?? 0) > 0)
            })

            const gridData = computed(() => getGridData(activeCart.value))
            const totalPrice = computed(() => gridData.value?.reduce((acc, item) => acc + item.amount, 0) ?? 0)
            const totalPriceText = computed(() => getTotalPriceText())
            const itemsWithPriceOnRequest = computed(() => gridData.value?.filter(item => item.hasPriceOnRequest) ?? [])
            const anyPriceOnRequest = computed(() => itemsWithPriceOnRequest.value.length > 0)
            const showPrices = computed(() => userStore.userData?.permissions?.priceAccessAllowed === true)
            const anyPartnerMaterial = computed(() => gridData.value?.some(item => !!item.partnerMaterialNo) ?? false)

            const sendByEmailConfirmationText = computed(() => {
                if (isNamedMode.value) {
                    return localize('ConfirmSendOrderProposal').replace('{0}', userStore.userData?.currentUser ?? '??')
                }
                return localize('confirmSendCart').replace('{0}', userStore.userData?.currentUser ?? '??')
            })

            const cardTitle = computed(() => {
                if (isNamedMode.value && orderProposal.value) {
                    return `${localize('ProposedOrder')} JSP-${String(orderProposal.value.orderProposalNo).padStart(7, '0')}`
                }
                return localize('sparePartsInShoppingCart')
            })

            const selectedItemCount = computed(() => {
                if (!gridData.value) return 0
                return gridData.value.filter(r => r.selected && !r.isIncludedPart).length
            })

            const addToMyCartText = computed(() =>
                localize('AddToMyCart').replace('{0}', selectedItemCount.value.toString())
            )

            const minCreateProposalDate = computed(() => {
                const tomorrow = new Date()
                tomorrow.setDate(tomorrow.getDate() + 1)
                tomorrow.setHours(0, 0, 0, 0)
                return tomorrow
            })

            const isCreateProposalValid = computed(() => {
                if (!createProposalName.value.trim()) return false
                const d = createProposalExpDate.value
                return d instanceof Date && !isNaN(d.getTime()) && d >= minCreateProposalDate.value
            })

            const gridColumns = computed(() => {
                if (isNarrowScreen.value) {
                    return [
                        {
                            field: 'dummy',
                            title: localize('description'),
                            cell: 'custom2'
                        }
                    ] as GridColumnProps[]
                }

                let columns: GridColumnProps[] = []

                // Select checkbox column (named mode only)
                if (isNamedMode.value) {
                    columns.push({
                        field: 'selected',
                        title: localize('Select'),
                        width: 70,
                        cell: 'custom1'
                    })
                }

                // Picture column (first visual column)
                columns.push({
                    field: 'picture',
                    title: ' ',
                    width: 110,
                    cell: 'custom1'
                })

                columns.push({
                    field: 'materialNumber',
                    title: localize('materialNo'),
                    width: 100,
                    cell: 'custom1'
                })

                // Own material number column (only when at least one item has one)
                if (anyPartnerMaterial.value) {
                    columns.push({
                        field: 'partnerMaterialNo',
                        title: localize('ownMaterialNo'),
                        width: 160,
                        cell: 'custom1'
                    })
                }

                columns.push({
                    field: 'shortText',
                    title: localize('description'),
                    cell: 'custom1'
                })

                columns.push(
                    {
                        field: 'countText',
                        title: localize('qty'),
                        width: 200,
                        cell: 'custom1'
                    }
                )

                if (showPrices.value) {
                    columns.push({
                        field: 'priceText',
                        title: localize('price'),
                        className: 'right-aligned',
                        width: 200,
                        cell: 'custom1',
                    },
                        {
                            field: 'amountText',
                            title: localize('amount'),
                            className: 'right-aligned font-bold',
                            width: 200,
                            cell: 'custom1'
                        })
                }

                return columns
            })

            const computedData = {
                userCurrency,
                isNarrowScreen,
                isAdmin,
                isNamedMode,
                priceFormatter,
                hasLoaded,
                gridData,
                totalPrice,
                totalPriceText,
                itemsWithPriceOnRequest,
                anyPriceOnRequest,
                showPrices,
                anyPartnerMaterial,
                sendByEmailConfirmationText,
                gridColumns,
                cardTitle,
                selectedItemCount,
                addToMyCartText,
                minCreateProposalDate,
                isCreateProposalValid
            }

            /********************************************************************************************************************
             PUBLIC METHODS AND EVENT HANDLERS
             ********************************************************************************************************************/
            const { localize, materialTextFor, materialSalesTextFor } = buildMaterialLocalization()
            const { localizeUnit3 } = buildUnitLocalization()

            function showResetCartConfirmation() {
                isResetCartConfirmationVisible.value = true
            }

            function closeResetCartConfirmation() {
                isResetCartConfirmationVisible.value = false
            }

            function onResetCart() {
                isUpdating.value = true
                closeResetCartConfirmation()

                shoppingCartStore.clear()
                    .then(() => {
                        notificationStore.addNotification(
                            new NotificationDTO(localize('ShoppingCartCleared'), 'success')
                        )
                    })
                    .finally(() => isUpdating.value = false)
            }

            function showSendCartConfirmation() {
                isSendCartConfirmationVisible.value = true
            }

            function closeSendCartConfirmation() {
                isSendCartConfirmationVisible.value = false
            }

            function onSendCart() {
                isUpdating.value = true
                closeSendCartConfirmation()

                if (isNamedMode.value && proposalId.value) {
                    orderProposalApi.sendByEmail(proposalId.value)
                        .then(() => {
                            notificationStore.addNotification(
                                new NotificationDTO(localize('EmailSent'), 'success')
                            )
                        })
                        .finally(() => isUpdating.value = false)
                } else {
                    shoppingCartStore.sendByEmail()
                        .then(() => {
                            notificationStore.addNotification(
                                new NotificationDTO(localize('EmailSent'), 'success')
                            )
                        })
                        .finally(() => isUpdating.value = false)
                }
            }

            function onCheckout() {
                router.push({ name: 'shopping-cart-checkout' })
            }

            function jumpToDetails(materialNumber: string) {
                router.push({ name: 'material', params: { matno: materialNumber } })
            }

            function getSerialNumberText(row: IShoppingCartRow) {
                switch (row.serialNumbers.length) {
                    case 0: return null
                    case 1: return `${localize('serialNumber')}: ${row.serialNumbers[0]}`
                    default: return `${localize('serialNumbers')}: ${row.serialNumbers.join(', ')}`
                }
            }

            function toggleSelection(row: IShoppingCartRow) {
                if (row.isIncludedPart) return
                if (selectedMaterials.value.has(row.materialNumber)) {
                    selectedMaterials.value.delete(row.materialNumber)
                } else {
                    selectedMaterials.value.add(row.materialNumber)
                }
            }

            function onAddToMyCart() {
                if (!proposalId.value || selectedItemCount.value === 0) return

                const materialNumbers = Array.from(selectedMaterials.value)
                const count = selectedItemCount.value

                isUpdating.value = true
                orderProposalApi.addToCart(proposalId.value, { materialNumbers })
                    .then(response => {
                        // Update the ordinary cart store with the response
                        shoppingCartStore._shoppingCart = response.data
                        notificationStore.addNotification(
                            new NotificationDTO(
                                localize('ItemsAddedToCart').replace('{0}', count.toString()),
                                'success'
                            )
                        )
                        // Navigate to ordinary cart
                        router.push({ name: 'shopping-cart-content' })
                    })
                    .finally(() => isUpdating.value = false)
            }

            // Create order proposal dialog
            function showCreateProposalDialog() {
                createProposalName.value = ''
                createProposalExpDate.value = getDefaultExpirationDate()
                isCreateProposalDialogVisible.value = true
            }

            function closeCreateProposalDialog() {
                isCreateProposalDialogVisible.value = false
            }

            function onCreateProposal() {
                if (!isCreateProposalValid.value) return

                isUpdating.value = true
                closeCreateProposalDialog()

                orderProposalApi.create({
                    name: createProposalName.value.trim(),
                    expirationDate: createProposalExpDate.value!.toISOString()
                })
                    .then(response => {
                        const created = response.data
                        createdProposalOrderProposalNo.value = created.orderProposalNo
                        createdProposalCartId.value = created.id
                        createdProposalName.value = created.name

                        notificationStore.addNotification(
                            new NotificationDTO(localize('OrderProposalCreated'), 'success')
                        )

                        // The ordinary cart has been emptied on the backend; reload it
                        shoppingCartStore.clear()
                    })
                    .finally(() => isUpdating.value = false)
            }

            const publicMethods = {
                localize,
                localizeUnit3,
                showResetCartConfirmation,
                closeResetCartConfirmation,
                onResetCart,
                showSendCartConfirmation,
                closeSendCartConfirmation,
                onSendCart,
                onCheckout,
                jumpToDetails,
                getSerialNumberText,
                toggleSelection,
                onAddToMyCart,
                showCreateProposalDialog,
                closeCreateProposalDialog,
                onCreateProposal,
                showProposalUrlDialog: () => isProposalUrlDialogVisible.value = true
            }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function getDefaultExpirationDate(): Date {
                const date = new Date()
                date.setDate(date.getDate() + 30)
                return date
            }

            function loadOrderProposal(id: string) {
                proposalError.value = false
                orderProposal.value = null

                orderProposalApi.getById(id)
                    .then(response => {
                        orderProposal.value = response.data
                        // Initialize all items as selected
                        selectedMaterials.value = new Set(
                            response.data.items.map(i => i.materialNumber)
                        )
                    })
                    .catch(() => {
                        proposalError.value = true
                    })
            }

            function getGridData(shoppingCart: IShoppingCartDTO | null): IShoppingCartRow[] | null {
                if (!allSpareParts.value?.length || shoppingCart == null) return null

                const result: IShoppingCartRow[] = []

                for (const item of shoppingCart.items) {
                    const bomHeader = getMaterial(item.materialNumber)

                    result.push(convert(item, bomHeader, false))

                    const includedParts = bomHeader?.bomInstances.find(b => b.bomUsage == DbConstants.materialBomUsage.RecommendedItems)

                    if (includedParts) {
                        for (const part of includedParts.components.filter(c => !c.material.hiddenSparePart)) {
                            const shopptingCartItemForIncludedPart: IShoppingCartItemDTO = {
                                materialNumber: part.material.materialNo,
                                count: part.isFixedQty ? item.count : part.qty * item.count,
                                serialNumbers: []
                            }

                            result.push(convert(shopptingCartItemForIncludedPart, part, true))
                        }
                    }
                }

                return result
            }

            function getMaterial(materialNumber: number): IBomHeaderDTO | null {
                if (!allSpareParts.value?.length) return null

                const bomHeader = sparePartMap.value?.get(materialNumber.toString())

                if (bomHeader != null) {
                    return bomHeader
                }

                notificationStore.addNotification(
                    NotificationDTO.createForDebug(`material not found in spare part store: ${materialNumber}`, 'error')
                )

                return null
            }

            function convert(item: IShoppingCartItemDTO,
                bomHeader: IBomHeaderDTO | null,
                includedPart: boolean): IShoppingCartRow {
                const material = bomHeader?.material

                if (material == null) {
                    return {
                        materialNumber: item.materialNumber,
                        price: 0,
                        priceText: '',
                        priceQty: 1,
                        priceQtyUnit: '',
                        count: item.count,
                        countUnitText: '',
                        amount: 0,
                        amountText: '',
                        hasPriceOnRequest: false,
                        salesText: '',
                        shortText: '?',
                        isSparePart: true,
                        sparePartStatus: '',
                        availableQty: 0,
                        isIncludedPart: false,
                        serialNumbers: [],
                        selected: isNamedMode.value && !includedPart,
                        materialDTO: null
                    } as IShoppingCartRow
                }

                const countUnitText = material.priceQtyUnit
                    ? `(${localizeUnit3(material.priceQtyUnit)})`
                    : ''

                const formatter = priceFormatter.value
                const { unitPrice: price, amount, hasPriceOnRequest } = includedPart
                    ? { unitPrice: 0, amount: 0, hasPriceOnRequest: false }
                    : cartPriceCalculator.computeItemPrice(item)

                const priceText = includedPart
                    ? '-'
                    : formatter({ ...material, price }).formattedPrice()

                const amountText = includedPart
                    ? '-'
                    : formatter({ ...material, price: amount }).formattedPrice()

                return {
                    materialNumber: item.materialNumber,
                    isSparePart: material.isSparePart,
                    sparePartStatus: material.sparePartStatus,
                    price: price,
                    priceQty: material.priceQty,
                    priceQtyUnit: material.priceQtyUnit,

                    shortText: materialTextFor(material),
                    salesText: materialSalesTextFor(material),
                    partnerMaterialNo: material.partnerMaterialNo,
                    count: item.count,
                    countUnitText,
                    hasPriceOnRequest,

                    priceText,
                    amount,
                    amountText,

                    availableQty: bomHeader?.availableStock,
                    isIncludedPart: includedPart,

                    serialNumbers: item.serialNumbers,
                    selected: isNamedMode.value && selectedMaterials.value.has(item.materialNumber) && !includedPart,
                    materialDTO: material
                } as IShoppingCartRow
            }

            function getTotalPriceText() {
                return cartPriceCalculator.computeTotalPriceText(activeCart.value?.items ?? [])
            }

            /********************************************************************************************************************
             LIFE-CYCLE HOOKS
             ********************************************************************************************************************/
            // Load order proposal when route has ?id= query param
            watch(proposalId, (newId) => {
                if (newId) {
                    loadOrderProposal(newId)
                } else {
                    orderProposal.value = null
                    proposalError.value = false
                }
            }, { immediate: true })

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
